import { useState, useEffect, useRef, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Snowflake, PlusCircle, Loader2, Search, PackageCheck,
  Wrench, AlertTriangle, Boxes, TrendingDown, Users, MapPin,
  Pencil, Trash2, Warehouse, X, ArrowLeftRight, Info,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

// ---------------- helpers ----------------
const STATUS_FREEZER: Record<string, { label: string; cls: string }> = {
  livre: { label: 'Livre (estoque)', cls: 'bg-emerald-100 text-emerald-700' },
  em_uso: { label: 'Em uso (cliente)', cls: 'bg-blue-100 text-blue-700' },
  manutencao: { label: 'Manutenção', cls: 'bg-amber-100 text-amber-700' },
  perdido: { label: 'Perdido', cls: 'bg-red-100 text-red-700' },
  baixado: { label: 'Baixado', cls: 'bg-gray-200 text-gray-600' },
}
const STATUS_MOV: Record<string, { label: string; cls: string }> = {
  solicitado: { label: 'Solicitado', cls: 'bg-amber-100 text-amber-700' },
  aprovado: { label: 'Aprovado', cls: 'bg-blue-100 text-blue-700' },
  executado: { label: 'Executado', cls: 'bg-emerald-100 text-emerald-700' },
  cancelado: { label: 'Cancelado', cls: 'bg-gray-200 text-gray-600' },
}
const TIPO_LABEL: Record<string, string> = {
  colocacao: 'Colocação (ponto novo)', recolhimento: 'Recolhimento', troca: 'Troca',
  manutencao_envio: 'Envio p/ manutenção', manutencao_retorno: 'Retorno de manutenção', baixa: 'Baixa',
}
// explicação de cada operação (mostrada no modal ao escolher o tipo)
const TIPO_DESC: Record<string, string> = {
  colocacao: 'Enviar um freezer do estoque para um cliente novo.',
  recolhimento: 'Trazer um freezer de volta do cliente para o estoque ou manutenção.',
  troca: 'Trocar o freezer do cliente: recolhe o atual e coloca outro no lugar.',
  manutencao_envio: 'Mandar um freezer para a manutenção (sai do estoque).',
  manutencao_retorno: 'Registrar o retorno do freezer consertado para o estoque.',
  baixa: 'Dar baixa definitiva: aparelho perdido, roubado ou inutilizado.',
}

// Banner explicativo no topo de cada aba — deixa claro o que a aba faz (e o que ela NÃO faz),
// pra ninguém confundir "cadastrar freezer" com "movimentar freezer".
function TabIntro({ icon, tone, titulo, desc, chips, nao }: {
  icon: ReactNode; tone: 'blue' | 'violet'; titulo: string; desc: string; chips?: string[]; nao?: string
}) {
  const grad = tone === 'blue'
    ? 'from-sky-50 to-blue-50 border-blue-100'
    : 'from-violet-50 to-fuchsia-50 border-violet-100'
  const badge = tone === 'blue' ? 'bg-blue-600' : 'bg-violet-600'
  const chipCls = tone === 'blue' ? 'text-blue-700 border-blue-200' : 'text-violet-700 border-violet-200'
  return (
    <div className={`rounded-xl border bg-gradient-to-r ${grad} p-4 flex gap-3`}>
      <div className={`w-10 h-10 rounded-lg ${badge} text-white grid place-items-center shrink-0`}>{icon}</div>
      <div className="space-y-1.5 min-w-0">
        <div className="font-semibold text-gray-800">{titulo}</div>
        <p className="text-sm text-gray-600">{desc}</p>
        {chips && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {chips.map(c => <span key={c} className={`text-[11px] bg-white/80 rounded-full px-2 py-0.5 border ${chipCls}`}>{c}</span>)}
          </div>
        )}
        {nao && (
          <p className="text-xs text-gray-500 flex items-start gap-1 pt-1">
            <Info size={13} className="mt-0.5 shrink-0" /> <span>{nao}</span>
          </p>
        )}
      </div>
    </div>
  )
}

function Chip({ map, v }: { map: Record<string, { label: string; cls: string }>; v: string }) {
  const it = map[v] || { label: v, cls: 'bg-gray-100 text-gray-600' }
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${it.cls}`}>{it.label}</span>
}

// busca de cliente (combobox simples)
function ClientePicker({ value, onChange }: { value: any; onChange: (c: any) => void }) {
  const [q, setQ] = useState('')
  const { data: lista = [] } = useQuery<any[]>({
    queryKey: ['comodato-clientes', q],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'cliente', q: q || undefined } })).data,
    enabled: q.length >= 2,
  })
  if (value) return (
    <div className="flex items-center gap-2 text-sm">
      <span className="font-medium">{value.nome}</span>
      <button onClick={() => onChange(null)} className="text-xs text-red-500">trocar</button>
    </div>
  )
  return (
    <div className="relative">
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar cliente (nome)…"
        onKeyDown={enterAbrePrimeiro(lista.slice(0, 20), c => { onChange(c); setQ('') })}
        className="w-full border rounded px-2 py-1.5 text-sm" />
      {q.length >= 2 && lista.length > 0 && (
        <div className="absolute z-20 bg-white border rounded shadow mt-1 w-full max-h-48 overflow-auto">
          {lista.slice(0, 20).map(c => (
            <button key={c.id} onClick={() => { onChange(c); setQ('') }}
              className="block w-full text-left px-2 py-1.5 text-sm hover:bg-blue-50">
              {c.nome}{c.cidade ? ` — ${c.cidade}/${c.uf || ''}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------- PAINEL ----------------
function Painel() {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['comodato-dashboard'],
    queryFn: async () => (await api.get('/comodato/dashboard')).data,
    refetchInterval: 60_000,
  })
  if (isLoading || !data) return <div className="p-6 text-gray-400"><Loader2 className="animate-spin" /></div>
  const c = data.cards
  const cards = [
    { k: 'em_uso', label: 'Em uso (clientes)', v: c.em_uso, icon: <Users size={16} />, cls: 'text-blue-600' },
    { k: 'livre', label: 'Livres (estoque)', v: c.livre, icon: <PackageCheck size={16} />, cls: 'text-emerald-600' },
    { k: 'manutencao', label: 'Em manutenção', v: c.manutencao, icon: <Wrench size={16} />, cls: 'text-amber-600' },
    { k: 'perdido', label: 'Perdidos', v: c.perdido, icon: <AlertTriangle size={16} />, cls: 'text-red-600' },
    { k: 'total', label: 'Parque ativo', v: c.total, icon: <Boxes size={16} />, cls: 'text-gray-700' },
  ]
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(k => (
          <div key={k.k} className="bg-white border rounded-xl p-3">
            <div className={`flex items-center gap-1.5 text-xs font-medium ${k.cls}`}>{k.icon}{k.label}</div>
            <div className="text-2xl font-bold mt-1">{k.v}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 text-sm">
        <div className="bg-white border rounded-xl px-4 py-2">Valor do parque: <b>{formatMoney(data.valor_parque)}</b></div>
        {(data.pendentes.solicitado > 0 || data.pendentes.aprovado > 0) && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-2">
            Pendentes: <b>{data.pendentes.solicitado}</b> a aprovar · <b>{data.pendentes.aprovado}</b> a executar
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Em uso por rota" data={data.por_rota} xKey="rota" />
        <ChartCard title="Parque por modelo" data={data.por_modelo} xKey="modelo" />
      </div>
      <div className="bg-white border rounded-xl p-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 mb-2">
          <MapPin size={16} /> Rotas atendidas ({data.por_rota.length}) — freezers em uso por rota
        </div>
        {data.por_rota.length === 0 ? <div className="text-xs text-gray-400">Sem rotas.</div> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-1 max-h-56 overflow-auto text-sm">
            {data.por_rota.map((r: any) => (
              <div key={r.rota} className="flex justify-between border-b py-0.5">
                <span className="truncate pr-2">{r.rota}</span><b>{r.qtd}</b>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border rounded-xl p-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-orange-600 mb-2">
          <TrendingDown size={16} /> Freezers parados (baixa média de compra — candidatos a recolher/realocar)
        </div>
        {data.parados.length === 0 ? <div className="text-xs text-gray-400">Nenhum freezer com média baixa.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b">
              <th className="py-1">Freezer</th><th>Modelo</th><th>Cliente</th><th className="text-right">Média compra</th>
            </tr></thead>
            <tbody>{data.parados.map((p: any) => (
              <tr key={p.freezer_id} className="border-b last:border-0">
                <td className="py-1 font-medium">{p.numero}</td><td>{p.modelo || '—'}</td>
                <td>{p.cliente_nome || '—'}</td>
                <td className="text-right text-orange-600">{formatMoney(p.media_compra || 0)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function ChartCard({ title, data, xKey }: { title: string; data: any[]; xKey: string }) {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6']
  const top = (data || []).slice(0, 10)
  const h = Math.max(140, top.length * 30 + 24)
  return (
    <div className="bg-white border rounded-xl p-3">
      <div className="text-sm font-semibold mb-2">{title}</div>
      {top.length === 0 ? <div className="text-xs text-gray-400 py-8 text-center">Sem dados</div> : (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={top} layout="vertical" margin={{ left: 4, right: 28, top: 2, bottom: 2 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey={xKey} width={108} tick={{ fontSize: 10 }} interval={0} />
            <Tooltip />
            <Bar dataKey="qtd" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 10, fill: '#374151' }}>
              {top.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// abre PDF autenticado (contrato / DANFE) em nova aba
function abrirPDF(path: string) {
  const token = localStorage.getItem('pain_token')
  fetch(path, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
    .then(b => { const u = URL.createObjectURL(b); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000) })
    .catch(e => toast.error(`Falha no PDF: ${e.message}`))
}

// ---------------- FREEZERS ----------------
function Freezers() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [vendedorId, setVendedorId] = useState('')
  const [cliente, setCliente] = useState<any>(null)
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['vendedores'], queryFn: async () => (await api.get('/vendas/vendedores')).data,
  })
  const { data: freezers = [], isLoading } = useQuery<any[]>({
    queryKey: ['comodato-freezers', status, q, vendedorId, cliente?.id],
    queryFn: async () => (await api.get('/comodato/freezers', { params: {
      status: status || undefined, q: q || undefined,
      vendedor_id: vendedorId || undefined, cliente_id: cliente?.id || undefined,
    } })).data,
  })
  const refresh = () => { qc.invalidateQueries({ queryKey: ['comodato-freezers'] }); qc.invalidateQueries({ queryKey: ['comodato-dashboard'] }) }
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/comodato/freezers/${id}`)).data,
    onSuccess: (d: any) => { toast.success(d?.modo === 'inativado' ? 'Freezer inativado (tinha histórico)' : 'Freezer excluído'); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })
  const gerarNf = useMutation({
    mutationFn: async (id: number) => (await api.post(`/comodato/freezers/${id}/gerar-nf`)).data,
    onSuccess: (d: any) => { d.autorizada ? toast.success(`NF comodato ${d.numero || ''} autorizada`) : toast.error(`NF não autorizada: ${d.xMotivo || d.motivo || d.erro || 'verifique certificado/ambiente'}`); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar NF'),
  })
  return (
    <div className="space-y-3">
      <TabIntro tone="blue" icon={<Boxes size={20} />}
        titulo="Freezers — o cadastro dos seus aparelhos"
        desc="Aqui ficam TODOS os freezers da empresa (o patrimônio). Você cadastra um aparelho que entrou no estoque, edita os dados, vê em qual cliente cada um está, imprime o contrato e emite a NF de remessa."
        chips={['Cadastrar / editar aparelho', 'Ver status e cliente atual', 'Contrato + NF de remessa']}
        nao="Para COLOCAR num cliente, TROCAR ou RECOLHER um freezer, use a aba “Solicitações” — é lá que o freezer se movimenta." />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Patrimônio / modelo / série"
            className="border rounded pl-7 pr-2 py-1.5 text-sm w-60" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_FREEZER).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={vendedorId} onChange={e => setVendedorId(e.target.value)} className="border rounded px-2 py-1.5 text-sm" title="Filtrar por vendedor">
          <option value="">Todos vendedores</option>
          {vendedores.map(v => <option key={v.id} value={v.id}>{v.codigo_vendedor ? `${v.codigo_vendedor} — ` : ''}{v.nome}</option>)}
        </select>
        <div className="w-56">
          {cliente
            ? <div className="flex items-center gap-2 text-sm border rounded px-2 py-1.5"><span className="truncate">{cliente.nome}</span><button onClick={() => setCliente(null)} className="text-xs text-red-500 ml-auto">x</button></div>
            : <ClientePicker value={null} onChange={setCliente} />}
        </div>
        <button onClick={() => setModal(true)}
          title="Cadastrar um freezer que entrou no estoque (compra/aquisição) — não envia a cliente"
          className="ml-auto flex items-center gap-1 bg-blue-600 text-white rounded px-3 py-1.5 text-sm">
          <PlusCircle size={15} /> Cadastrar freezer no estoque
        </button>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 bg-gray-50 border-b">
            <th className="py-2 px-3">Patrimônio</th><th>Modelo</th><th>Marca</th><th>Litros</th>
            <th>Status</th><th>Cliente atual</th><th>Vend.</th><th className="text-right px-3">Valor</th><th className="text-right px-3">Ações</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={9} className="p-6 text-center text-gray-400"><Loader2 className="animate-spin inline" /></td></tr>
              : freezers.length === 0 ? <tr><td colSpan={9} className="p-6 text-center text-gray-400">Nenhum freezer.</td></tr>
                : freezers.map(f => (
                  <tr key={f.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{f.numero}</td><td>{f.modelo || '—'}</td>
                    <td>{f.marca || '—'}</td><td>{f.capacidade_litros || '—'}</td>
                    <td><Chip map={STATUS_FREEZER} v={f.status} /></td>
                    <td>{f.cliente_nome || '—'}</td>
                    <td className="text-center">{f.vendedor_codigo || '—'}</td>
                    <td className="text-right px-3">{formatMoney(f.valor)}</td>
                    <td className="text-right px-3 whitespace-nowrap">
                      {f.status === 'em_uso' && (
                        <button onClick={() => abrirPDF(`/api/comodato/freezers/${f.id}/contrato`)}
                          className="text-indigo-600 text-xs font-medium mr-3">Contrato</button>
                      )}
                      {f.status === 'em_uso' && !f.nf_numero && (
                        <button onClick={() => abrirPDF(`/api/comodato/freezers/${f.id}/preview-nf`)}
                          className="text-amber-600 text-xs font-medium mr-3"
                          title="Pré-visualizar a NF de comodato (CFOP/impostos) sem emitir">Preview NF</button>
                      )}
                      {f.status === 'em_uso' && (f.nf_numero
                        ? <button onClick={() => abrirPDF(`/api/nfe-proprio/danfe-autorizado/${f.nf_id}`)}
                          className="text-emerald-600 text-xs font-medium mr-3">Nota {f.nf_numero}</button>
                        : <button onClick={() => gerarNf.mutate(f.id)} disabled={gerarNf.isPending}
                          className="text-emerald-600 text-xs font-medium mr-3">Gerar NF</button>)}
                      <button onClick={() => setEditing(f)} className="text-blue-600 text-xs font-medium mr-3">Editar</button>
                      <button disabled={f.status === 'em_uso'} title={f.status === 'em_uso' ? 'Recolha antes de excluir' : ''}
                        onClick={() => pedirExclusao({ descricao: `o freezer ${f.numero}`, onConfirm: () => excluir.mutate(f.id) })}
                        className="text-red-500 text-xs disabled:opacity-30 disabled:cursor-not-allowed">Excluir</button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {(modal || editing) && <NovoFreezerModal freezer={editing} onClose={() => { setModal(false); setEditing(null) }} onSaved={refresh} />}
    </div>
  )
}

function NovoFreezerModal({ freezer, onClose, onSaved }: { freezer?: any; onClose: () => void; onSaved: () => void }) {
  const ed = !!freezer?.id
  const [f, setF] = useState<any>({
    numero: freezer?.numero || '', produto_id: freezer?.produto_id || '', modelo: freezer?.modelo || '', marca: freezer?.marca || '',
    capacidade_litros: freezer?.capacidade_litros || '', voltagem: freezer?.voltagem || '',
    valor: freezer?.valor ?? '', numero_serie: freezer?.numero_serie || '',
    data_aquisicao: freezer?.data_aquisicao || '', nf_entrada: freezer?.nf_entrada || '',
    status: freezer?.status || 'livre', observacao: freezer?.observacao || '',
    comodato_deposito_id: freezer?.comodato_deposito_id || '',
  })
  const set = (k: string, v: any) => setF((s: any) => ({ ...s, [k]: v }))
  const [cli, setCli] = useState<any>(freezer?.cliente_id ? { id: freezer.cliente_id, nome: freezer.cliente_nome } : null)
  const { data: depositos = [] } = useQuery<any[]>({
    queryKey: ['comodato-depositos'],
    queryFn: async () => (await api.get('/comodato/depositos')).data,
  })
  const { data: modelos = [] } = useQuery<any[]>({
    queryKey: ['comodato-modelos'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { finalidade: 'comodato' } })).data,
  })
  // preços por modelo, pra sugerir o valor do bem automaticamente
  const { data: precos = [] } = useQuery<any[]>({
    queryKey: ['modelos-precos'],
    queryFn: async () => (await api.get('/comodato/modelos-precos')).data,
  })
  const precoDoModelo = (pid: any) => {
    const r = precos.find((x: any) => String(x.produto_id) === String(pid))
    return r ? Number(r.preco || 0) : 0
  }
  const salvar = useMutation({
    mutationFn: async () => {
      const body = { ...f, capacidade_litros: f.capacidade_litros ? Number(f.capacidade_litros) : null,
        valor: Number(f.valor || 0), produto_id: f.produto_id ? Number(f.produto_id) : null,
        comodato_deposito_id: f.comodato_deposito_id ? Number(f.comodato_deposito_id) : null,
        cliente_id: cli?.id || null, data_aquisicao: f.data_aquisicao || null }
      return ed ? (await api.put(`/comodato/freezers/${freezer.id}`, body)).data
        : (await api.post('/comodato/freezers', body)).data
    },
    onSuccess: () => { toast.success(ed ? 'Freezer atualizado' : 'Freezer cadastrado'); onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  return (
    <Modal title={ed ? `Editar freezer ${freezer.numero}` : 'Novo freezer (entrada no patrimônio)'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Patrimônio / Nº *"><input className="input" value={f.numero} onChange={e => set('numero', e.target.value)} /></Field>
        <Field label="Nº de série"><input className="input" value={f.numero_serie} onChange={e => set('numero_serie', e.target.value)} /></Field>
        <Field label="Modelo (produto comodato)">
          <select className="input" value={f.produto_id} onChange={e => {
            const pid = e.target.value; const m = modelos.find((x: any) => String(x.id) === pid)
            const pm = precoDoModelo(pid)
            setF((s: any) => ({
              ...s, produto_id: pid,
              modelo: m ? (m.descricao || s.modelo) : s.modelo,
              // sugere o valor do modelo só se ainda não há valor digitado (não sobrescreve)
              valor: (!s.valor || Number(s.valor) === 0) && pm > 0 ? pm : s.valor,
            }))
          }}>
            <option value="">— sem produto / avulso —</option>
            {modelos.map((m: any) => <option key={m.id} value={m.id}>{m.descricao}</option>)}
          </select>
        </Field>
        <Field label="Marca"><input className="input" value={f.marca} onChange={e => set('marca', e.target.value)} /></Field>
        <Field label="Descrição/modelo (livre)"><input className="input" value={f.modelo} onChange={e => set('modelo', e.target.value)} /></Field>
        <Field label="Capacidade (litros)"><input type="number" className="input" value={f.capacidade_litros} onChange={e => set('capacidade_litros', e.target.value)} /></Field>
        <Field label="Voltagem"><select className="input" value={f.voltagem} onChange={e => set('voltagem', e.target.value)}><option value="">—</option><option>110</option><option>220</option><option>bivolt</option></select></Field>
        <Field label="Valor do bem (R$)"><input type="number" className="input" value={f.valor} onChange={e => set('valor', e.target.value)} /></Field>
        <Field label="Data de aquisição"><input type="date" className="input" value={f.data_aquisicao} onChange={e => set('data_aquisicao', e.target.value)} /></Field>
        <Field label="NF de compra"><input className="input" value={f.nf_entrada} onChange={e => set('nf_entrada', e.target.value)} /></Field>
        <Field label="Status"><select className="input" value={f.status} onChange={e => set('status', e.target.value)}>{Object.entries(STATUS_FREEZER).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Field>
        <Field label="Depósito (onde está disponível)"><select className="input" value={f.comodato_deposito_id} onChange={e => set('comodato_deposito_id', e.target.value)}><option value="">—</option>{depositos.map((d: any) => <option key={d.id} value={d.id}>{d.nome}</option>)}</select></Field>
        <div className="col-span-2"><Field label="Cliente (quando em uso) — busque e corrija aqui"><ClientePicker value={cli} onChange={setCli} /></Field></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border">Cancelar</button>
        <button disabled={!f.numero || salvar.isPending} onClick={() => salvar.mutate()}
          className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50">
          {salvar.isPending ? 'Salvando…' : (ed ? 'Salvar' : 'Cadastrar')}
        </button>
      </div>
    </Modal>
  )
}

// ---------------- SOLICITAÇÕES ----------------
function Solicitacoes() {
  const qc = useQueryClient()
  const [modal, setModal] = useState(false)
  const { data: movs = [], isLoading } = useQuery<any[]>({
    queryKey: ['comodato-movimentos'],
    queryFn: async () => (await api.get('/comodato/movimentos')).data,
  })
  const refresh = () => { qc.invalidateQueries({ queryKey: ['comodato-movimentos'] }); qc.invalidateQueries({ queryKey: ['comodato-dashboard'] }); qc.invalidateQueries({ queryKey: ['comodato-freezers'] }) }
  const acao = useMutation({
    mutationFn: async ({ id, op }: { id: number; op: string }) =>
      (await api.post(`/comodato/movimentos/${id}/${op}`)).data,
    onSuccess: () => { toast.success('Feito'); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const gerarNf = useMutation({
    mutationFn: async ({ id, nf_referencia, sem_nf_remessa }: { id: number; nf_referencia?: string; sem_nf_remessa?: boolean }) =>
      (await api.post(`/comodato/movimentos/${id}/gerar-nf`, { nf_referencia, sem_nf_remessa })).data,
    onSuccess: (d: any) => {
      const r = d.resultados || {}
      const parts = Object.entries(r).map(([k, v]: any) =>
        `${k === 'remessa' ? 'Remessa' : 'Retorno'}: ${v.autorizada ? `${v.numero || ''} ✓` : `falhou (${v.motivo || v.erro || 'erro'})`}`)
      const ok = Object.values(r).every((v: any) => v.autorizada)
      if (parts.length === 0) toast.success(d.autorizada ? `NF ${d.numero || ''} autorizada` : 'Nada a emitir')
      else if (ok) toast.success(`NF comodato — ${parts.join(' | ')}`)
      else toast.error(parts.join(' | '))
      refresh()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar NF'),
  })
  // Retorno (1908) exige a NF de remessa ORIGINAL (refNFe) → abre o modal inteligente
  // (buscar no sistema / importar XML / digitar chave). Remessa pura emite direto.
  const [refMov, setRefMov] = useState<any>(null)
  const gerarComRef = (m: any) => {
    const precisaRef = (m.tipo === 'recolhimento' && !m.nf_numero) || (m.tipo === 'troca' && !m.nf_retorno_numero)
    if (precisaRef) { setRefMov(m); return }
    gerarNf.mutate({ id: m.id })
  }
  return (
    <div className="space-y-3">
      <TabIntro tone="violet" icon={<ArrowLeftRight size={20} />}
        titulo="Solicitações — a movimentação dos freezers"
        desc="É aqui que o freezer ANDA: colocar num cliente novo, trocar, recolher, mandar/voltar da manutenção ou dar baixa. Cada operação segue o fluxo Solicita → Aprova → Executa (e aí o status do freezer muda sozinho)."
        chips={['Colocação', 'Troca', 'Recolhimento', 'Manutenção', 'Baixa']}
        nao="Só quer cadastrar ou corrigir os dados de um aparelho (sem mexer em cliente)? Isso é na aba “Freezers”." />
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Fluxo: <strong className="text-amber-600">Solicitado</strong> → <strong className="text-blue-600">Aprovado</strong> → <strong className="text-emerald-600">Executado</strong></span>
        <button onClick={() => setModal(true)}
          title="Registrar uma movimentação: colocar, trocar, recolher, manutenção ou baixa"
          className="ml-auto flex items-center gap-1 bg-violet-600 text-white rounded px-3 py-1.5 text-sm">
          <PlusCircle size={15} /> Nova movimentação
        </button>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 bg-gray-50 border-b">
            <th className="py-2 px-3">Nº</th><th>Tipo</th><th>Cliente</th><th>Status</th>
            <th>Solicitado</th><th className="text-right px-3">Ações</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={6} className="p-6 text-center text-gray-400"><Loader2 className="animate-spin inline" /></td></tr>
              : movs.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">Nenhuma movimentação ainda.<br /><span className="text-xs">Clique em <strong>“Nova movimentação”</strong> para colocar, trocar ou recolher um freezer.</span></td></tr>
                : movs.map(m => (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{m.numero}</td>
                    <td>{TIPO_LABEL[m.tipo] || m.tipo}</td>
                    <td>{m.cliente_nome || '—'}</td>
                    <td><Chip map={STATUS_MOV} v={m.status} /></td>
                    <td className="text-xs text-gray-500">{m.data_solicitacao}</td>
                    <td className="text-right px-3 whitespace-nowrap">
                      {m.status === 'solicitado' && (
                        <button onClick={() => acao.mutate({ id: m.id, op: 'aprovar?executar=true' })}
                          className="text-emerald-600 text-xs font-medium mr-2">Aprovar e executar</button>
                      )}
                      {m.status === 'aprovado' && (
                        <button onClick={() => acao.mutate({ id: m.id, op: 'executar' })}
                          className="text-emerald-600 text-xs font-medium mr-2">Executar</button>
                      )}
                      {m.status !== 'executado' && m.status !== 'cancelado' && (
                        <button onClick={() => acao.mutate({ id: m.id, op: 'cancelar' })}
                          className="text-red-500 text-xs">Cancelar</button>
                      )}
                      {m.status === 'executado' && ['colocacao', 'troca', 'recolhimento'].includes(m.tipo) && (() => {
                        // Recolhimento: nf_numero É o retorno (1908). Colocação: nf_numero é a remessa (5908).
                        // Troca: nf_numero = remessa (freezer novo), nf_retorno_numero = retorno (freezer recolhido).
                        const temRemessa = ['colocacao', 'troca'].includes(m.tipo)
                        const remessaFeita = !!m.nf_numero
                        const retornoFeito = m.tipo === 'troca' ? !!m.nf_retorno_numero : (m.tipo === 'recolhimento' && !!m.nf_numero)
                        const precisaRemessa = temRemessa && !remessaFeita
                        const precisaRetorno = (m.tipo === 'troca' && !m.nf_retorno_numero) || (m.tipo === 'recolhimento' && !m.nf_numero)
                        return (
                          <span className="inline-flex items-center gap-2">
                            {temRemessa && remessaFeita && (
                              <button onClick={() => abrirPDF(`/api/nfe-proprio/danfe-autorizado/${m.nf_id}`)}
                                className="text-emerald-600 text-xs font-medium underline" title="DANFE remessa (5908)">
                                {m.tipo === 'troca' ? `Remessa ${m.nf_numero}` : `NF ${m.nf_numero}`}</button>
                            )}
                            {m.tipo === 'troca' && m.nf_retorno_numero && (
                              <button onClick={() => abrirPDF(`/api/nfe-proprio/danfe-autorizado/${m.nf_retorno_id}`)}
                                className="text-emerald-600 text-xs font-medium underline" title="DANFE retorno (1908)">Retorno {m.nf_retorno_numero}</button>
                            )}
                            {m.tipo === 'recolhimento' && retornoFeito && (
                              <button onClick={() => abrirPDF(`/api/nfe-proprio/danfe-autorizado/${m.nf_id}`)}
                                className="text-emerald-600 text-xs font-medium underline" title="DANFE retorno (1908)">NF {m.nf_numero}</button>
                            )}
                            {precisaRetorno && (
                              <button onClick={() => abrirPDF(`/api/comodato/movimentos/${m.id}/preview-nf?leg=retorno`)}
                                className="text-gray-500 text-xs" title="Pré-visualizar a NF de retorno (1908) sem emitir">Preview retorno</button>
                            )}
                            {(precisaRemessa || precisaRetorno) && (
                              <button onClick={() => gerarComRef(m)} disabled={gerarNf.isPending}
                                className="text-blue-600 text-xs font-medium">
                                {m.tipo === 'recolhimento' ? 'Gerar NF retorno'
                                  : m.tipo === 'troca' ? (remessaFeita ? 'Gerar NF retorno' : 'Gerar NFs')
                                    : 'Gerar NF'}</button>
                            )}
                          </span>
                        )
                      })()}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {modal && <NovaSolicitacaoModal onClose={() => setModal(false)} onSaved={refresh} />}
      {refMov && <RefNotaModal mov={refMov}
        onClose={() => setRefMov(null)}
        onConfirm={(chave, semNf) => { gerarNf.mutate({ id: refMov.id, nf_referencia: chave, sem_nf_remessa: semNf }); setRefMov(null) }} />}
    </div>
  )
}

function NovaSolicitacaoModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [tipo, setTipo] = useState('colocacao')
  const [cliente, setCliente] = useState<any>(null)
  const [m, setM] = useState<any>({ freezer_id: '', freezer_recolher_id: '', media_compra: '', motivo: '', destino: 'estoque', modelo_solicitado: '', vendedor_id: '' })
  const set = (k: string, v: any) => setM((s: any) => ({ ...s, [k]: v }))

  const { data: livres = [] } = useQuery<any[]>({ queryKey: ['comodato-disp'], queryFn: async () => (await api.get('/comodato/freezers/disponiveis')).data })
  const { data: noCliente = [] } = useQuery<any[]>({
    queryKey: ['comodato-do-cliente', cliente?.id],
    queryFn: async () => (await api.get('/comodato/freezers', { params: { status: 'em_uso', cliente_id: cliente?.id } })).data,
    enabled: !!cliente && (tipo === 'recolhimento' || tipo === 'troca'),
  })
  const { data: vendedores = [] } = useQuery<any[]>({ queryKey: ['vendedores'], queryFn: async () => (await api.get('/vendas/vendedores')).data })

  const precisaCliente = tipo === 'colocacao' || tipo === 'recolhimento' || tipo === 'troca'
  const precisaColocar = tipo === 'colocacao' || tipo === 'troca'
  const precisaRecolher = tipo === 'recolhimento' || tipo === 'troca'

  const salvar = useMutation({
    mutationFn: async () => (await api.post('/comodato/movimentos', {
      tipo, cliente_id: cliente?.id || null,
      freezer_id: m.freezer_id ? Number(m.freezer_id) : null,
      freezer_recolher_id: m.freezer_recolher_id ? Number(m.freezer_recolher_id) : null,
      vendedor_id: m.vendedor_id ? Number(m.vendedor_id) : null,
      media_compra: m.media_compra ? Number(m.media_compra) : null,
      motivo: m.motivo || null, destino: precisaRecolher ? m.destino : null,
      modelo_solicitado: m.modelo_solicitado || null,
    })).data,
    onSuccess: () => { toast.success('Solicitação criada'); onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const valido = precisaCliente ? !!cliente : true
  return (
    <Modal title="Nova movimentação de freezer" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Field label="Operação">
          <select className="input" value={tipo} onChange={e => setTipo(e.target.value)}>
            <option value="colocacao">Colocação (ponto novo)</option>
            <option value="recolhimento">Recolhimento</option>
            <option value="troca">Troca</option>
            <option value="manutencao_envio">Envio p/ manutenção</option>
            <option value="manutencao_retorno">Retorno de manutenção</option>
            <option value="baixa">Baixa (perdido/inutilizado)</option>
          </select>
        </Field>
        <p className="text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 flex items-start gap-1.5 -mt-1">
          <Info size={13} className="mt-0.5 shrink-0" /> <span>{TIPO_DESC[tipo]}</span>
        </p>
        {precisaCliente && <Field label="Cliente *"><ClientePicker value={cliente} onChange={setCliente} /></Field>}
        {precisaRecolher && (
          <Field label="Freezer a recolher (do cliente)">
            <select className="input" value={m.freezer_recolher_id || m.freezer_id} onChange={e => set(tipo === 'troca' ? 'freezer_recolher_id' : 'freezer_id', e.target.value)}>
              <option value="">Selecione…</option>
              {noCliente.map(f => <option key={f.id} value={f.id}>{f.numero} — {f.modelo || ''}</option>)}
            </select>
          </Field>
        )}
        {(tipo === 'manutencao_envio' || tipo === 'manutencao_retorno' || tipo === 'baixa') && (
          <Field label="Freezer">
            <select className="input" value={m.freezer_id} onChange={e => set('freezer_id', e.target.value)}>
              <option value="">Selecione…</option>
              {(tipo === 'manutencao_retorno' ? livres : livres).map(f => <option key={f.id} value={f.id}>{f.numero} — {f.modelo || ''}</option>)}
            </select>
          </Field>
        )}
        {precisaColocar && (
          <Field label="Freezer a colocar (estoque livre)">
            <select className="input" value={m.freezer_id} onChange={e => set('freezer_id', e.target.value)}>
              <option value="">Selecione…</option>
              {livres.map(f => <option key={f.id} value={f.id}>{f.numero} — {f.modelo || ''}</option>)}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          {precisaCliente && <Field label="Média de compra (R$)"><input type="number" className="input" value={m.media_compra} onChange={e => set('media_compra', e.target.value)} /></Field>}
          <Field label="Vendedor"><select className="input" value={m.vendedor_id} onChange={e => set('vendedor_id', e.target.value)}><option value="">—</option>{vendedores.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}</select></Field>
          {precisaRecolher && <Field label="Destino do recolhido"><select className="input" value={m.destino} onChange={e => set('destino', e.target.value)}><option value="estoque">Estoque (livre)</option><option value="manutencao">Manutenção</option><option value="perdido">Perdido</option></select></Field>}
        </div>
        <Field label={tipo === 'troca' || tipo === 'recolhimento' ? 'Motivo' : 'Observação'}><textarea className="input" rows={2} value={m.motivo} onChange={e => set('motivo', e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border">Cancelar</button>
        <button disabled={!valido || salvar.isPending} onClick={() => salvar.mutate()}
          className="px-3 py-1.5 text-sm rounded bg-violet-600 text-white disabled:opacity-50">
          {salvar.isPending ? 'Enviando…' : 'Criar movimentação'}
        </button>
      </div>
    </Modal>
  )
}

// ---------------- MAPA (Leaflet via CDN) ----------------
let _leaflet: Promise<any> | null = null
function ensureLeaflet(): Promise<any> {
  if ((window as any).L) return Promise.resolve((window as any).L)
  if (_leaflet) return _leaflet
  _leaflet = new Promise((resolve, reject) => {
    const css = document.createElement('link')
    css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(css)
    const s = document.createElement('script')
    s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    s.onload = () => resolve((window as any).L); s.onerror = reject
    document.head.appendChild(s)
  })
  return _leaflet
}

function Mapa() {
  const { data } = useQuery<any>({ queryKey: ['comodato-mapa'], queryFn: async () => (await api.get('/comodato/mapa')).data })
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  useEffect(() => {
    if (!data || !ref.current) return
    let cancel = false
    ensureLeaflet().then((L) => {
      if (cancel || !ref.current) return
      if (!mapRef.current) {
        mapRef.current = L.map(ref.current).setView([-7.0, -39.3], 7)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '© OpenStreetMap' }).addTo(mapRef.current)
      }
      const map = mapRef.current
      if (map._frz) map.removeLayer(map._frz)
      const grp = L.layerGroup().addTo(map); map._frz = grp
      const pts = data.pontos || []
      const max = Math.max(1, ...pts.map((p: any) => p.qtd))
      const ll: any[] = []
      pts.forEach((p: any) => {
        const r = 6 + 24 * Math.sqrt(p.qtd / max)
        const cor = p.qtd >= 30 ? '#ef4444' : p.qtd >= 10 ? '#f59e0b' : '#2563eb'
        L.circleMarker([p.lat, p.lon], { radius: r, color: cor, weight: 1, fillColor: cor, fillOpacity: 0.5 })
          .bindPopup(`<b>${p.cidade}/${p.uf || ''}</b><br/>${p.qtd} freezers em uso<br/>R$ ${Number(p.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)
          .addTo(grp)
        ll.push([p.lat, p.lon])
      })
      if (ll.length) map.fitBounds(ll, { padding: [30, 30] })
      setTimeout(() => map.invalidateSize(), 250)
    })
    return () => { cancel = true }
  }, [data])
  return (
    <div className="space-y-2">
      <div className="text-sm text-gray-600">
        {data ? `${data.total_pontos} cidades · ${data.total_freezers} freezers em uso${data.sem_coordenada ? ` · ${data.sem_coordenada} sem coordenada (cidade não localizada)` : ''}` : 'carregando mapa…'}
        <span className="ml-3 text-xs text-gray-400">● azul &lt;10 · ● amarelo 10-29 · ● vermelho ≥30</span>
      </div>
      <div ref={ref} className="rounded-xl border" style={{ height: '70vh', width: '100%' }} />
    </div>
  )
}

// ---------------- shell ----------------
function Modal({ title, children, onClose }: { title: string; children: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="text-base font-semibold mb-3">{title}</div>
        {children}
      </div>
    </div>
  )
}

// Escolhe a NF de remessa de REFERÊNCIA do retorno — 3 jeitos, sem engessar:
//  1) Buscar no sistema (nº / cliente / freezer) → traz a chave sozinho
//  2) Importar o XML (de outro sistema) → extrai a chave
//  3) Digitar a chave (44 díg) na unha
function RefNotaModal({ mov, onClose, onConfirm }: { mov: any; onClose: () => void; onConfirm: (chave: string, semNf: boolean) => void }) {
  const [mode, setMode] = useState<'buscar' | 'xml' | 'chave'>('buscar')
  const [chave, setChave] = useState('')
  const [origemChave, setOrigemChave] = useState('')   // rótulo do que foi escolhido
  const [busca, setBusca] = useState('')
  const [semNf, setSemNf] = useState(false)            // freezer migrado sem remessa no sistema

  // sugestão automática: a remessa original do freezer que será devolvido
  useEffect(() => {
    api.get(`/comodato/movimentos/${mov.id}/ref-sugerida`).then(r => {
      if (r.data?.chave) { setChave(r.data.chave); setOrigemChave(`sugerida — NF ${r.data.numero || ''} do freezer`) }
    }).catch(() => { /* sem sugestão */ })
  }, [mov.id])

  const { data: resultados = [], isFetching } = useQuery<any[]>({
    queryKey: ['remessas-comodato', busca],
    queryFn: async () => (await api.get('/comodato/remessas-comodato', { params: { q: busca || undefined } })).data,
    enabled: mode === 'buscar',
  })

  const lerXml = async (file: File) => {
    try {
      const txt = await file.text()
      const m = txt.match(/Id="NFe(\d{44})"/) || txt.match(/<chNFe>(\d{44})<\/chNFe>/) || txt.match(/\b(\d{44})\b/)
      if (!m) { toast.error('Não encontrei a chave de 44 dígitos nesse XML.'); return }
      setChave(m[1]); setOrigemChave(`XML: ${file.name}`)
      toast.success('Chave extraída do XML')
    } catch { toast.error('Falha ao ler o arquivo XML') }
  }

  const chaveLimpa = chave.replace(/\D/g, '')
  const valido = semNf || chaveLimpa.length === 44
  const aba = (k: typeof mode, label: string) => (
    <button onClick={() => setMode(k)}
      className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${mode === k ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500'}`}>{label}</button>
  )
  return (
    <Modal title="NF de remessa de referência (para o retorno)" onClose={onClose}>
      <p className="text-xs text-gray-500 mb-3">O retorno aponta para a NF de remessa original do freezer. Escolha do jeito que for mais fácil:</p>

      {/* Freezer antigo/migrado que nunca teve remessa no sistema → gera sem referência */}
      <label className="flex items-start gap-2 mb-3 p-2.5 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={semNf} onChange={e => setSemNf(e.target.checked)} />
        <span className="text-xs text-amber-800">
          <strong>Sem NF de remessa</strong> — freezer antigo/migrado que nunca teve nota de remessa no sistema.
          Gera o retorno <strong>sem referência</strong> (registra no complemento da nota).
        </span>
      </label>

      {!semNf && (<>
      <div className="flex gap-1 border-b mb-3">
        {aba('buscar', '🔎 Buscar no sistema')}
        {aba('xml', '📎 Importar XML')}
        {aba('chave', '⌨️ Digitar chave')}
      </div>

      {mode === 'buscar' && (
        <div className="space-y-2">
          <input autoFocus value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(resultados, (r: any) => { setChave(r.chave); setOrigemChave(`NF ${r.numero} — ${r.cliente || ''}`) })}
            placeholder="Nº da NF, cliente ou freezer (patrimônio/modelo)…"
            className="border rounded w-full px-3 py-1.5 text-sm" />
          <div className="border rounded-lg max-h-60 overflow-auto divide-y">
            {isFetching ? <div className="p-4 text-center text-gray-400"><Loader2 size={16} className="animate-spin inline" /></div>
              : resultados.length === 0 ? <div className="p-4 text-center text-gray-400 text-sm">Nenhuma remessa encontrada.</div>
                : resultados.map((r: any) => (
                  <button key={r.chave} onClick={() => { setChave(r.chave); setOrigemChave(`NF ${r.numero} — ${r.cliente || ''}`) }}
                    className={`w-full text-left px-3 py-2 hover:bg-violet-50 ${chaveLimpa === r.chave ? 'bg-violet-100' : ''}`}>
                    <div className="text-sm font-medium">NF {r.numero} <span className="text-gray-400 font-normal">· {r.data || ''}</span></div>
                    <div className="text-xs text-gray-500 truncate">{r.cliente || '—'}{r.freezer ? ` · ${r.freezer}` : ''}</div>
                    <div className="text-[10px] text-gray-400 font-mono">…{String(r.chave).slice(-12)}</div>
                  </button>
                ))}
          </div>
        </div>
      )}

      {mode === 'xml' && (
        <div className="space-y-2">
          <label className="block border-2 border-dashed border-violet-200 rounded-lg p-6 text-center cursor-pointer hover:bg-violet-50">
            <input type="file" accept=".xml,text/xml" className="hidden"
              onChange={e => e.target.files?.[0] && lerXml(e.target.files[0])} />
            <div className="text-sm text-violet-700 font-medium">Clique para escolher o XML da remessa</div>
            <div className="text-xs text-gray-400 mt-1">de outro sistema — eu extraio a chave de 44 dígitos</div>
          </label>
        </div>
      )}

      {mode === 'chave' && (
        <div className="space-y-1">
          <input autoFocus value={chave} onChange={e => { setChave(e.target.value); setOrigemChave('digitada') }}
            placeholder="Chave de acesso (44 dígitos)" className="border rounded w-full px-3 py-1.5 text-sm font-mono" />
          <div className={`text-xs ${chaveLimpa.length === 44 ? 'text-emerald-600' : 'text-gray-400'}`}>{chaveLimpa.length}/44 dígitos</div>
        </div>
      )}

      {!semNf && chaveLimpa.length === 44 && (
        <div className="mt-3 text-xs bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
          <div className="text-emerald-700 font-medium">Chave selecionada {origemChave ? `(${origemChave})` : ''}</div>
          <div className="font-mono text-gray-500 break-all">{chaveLimpa}</div>
        </div>
      )}
      </>)}

      {semNf && (
        <div className="text-xs bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-amber-800">
          Vai gerar o retorno <strong>sem NF de referência</strong>. O complemento da nota registra que o bem estava em comodato sem nota de remessa anterior.
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border">Cancelar</button>
        <button disabled={!valido} onClick={() => onConfirm(chaveLimpa, semNf)}
          className="px-3 py-1.5 text-sm rounded bg-violet-600 text-white disabled:opacity-50">Gerar retorno</button>
      </div>
    </Modal>
  )
}
function Field({ label, children }: { label: string; children: any }) {
  return <label className="block"><span className="block text-xs text-gray-500 mb-0.5">{label}</span>{children}</label>
}

// ---------------- Pré-cadastros (fila do vendedor na rua) ----------------
function PreCadastros() {
  const qc = useQueryClient()
  const { data: lista = [], isLoading } = useQuery<any[]>({
    queryKey: ['precad', 'pendente'],
    queryFn: async () => (await api.get('/comodato/pre-cadastros', { params: { status: 'pendente' } })).data,
  })
  const aprovar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/comodato/pre-cadastros/${id}/aprovar`)).data,
    onSuccess: (d: any) => {
      toast.success(d?.obs || 'Aprovado — virou freezer')
      qc.invalidateQueries({ queryKey: ['precad'] }); qc.invalidateQueries({ queryKey: ['freezers'] })
      qc.invalidateQueries({ queryKey: ['precad-cont'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao aprovar'),
  })
  const rejeitar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/comodato/pre-cadastros/${id}/rejeitar`)).data,
    onSuccess: () => { toast.success('Rejeitado'); qc.invalidateQueries({ queryKey: ['precad'] }); qc.invalidateQueries({ queryKey: ['precad-cont'] }) },
  })
  const url = `${window.location.origin}/m/freezer`
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm flex flex-wrap items-center gap-2">
        <span className="font-medium text-blue-800">Link p/ o vendedor cadastrar na rua:</span>
        <code className="bg-white border rounded px-2 py-1 text-xs">{url}</code>
        <button onClick={() => { navigator.clipboard?.writeText(url); toast.success('Link copiado') }}
          className="text-xs bg-blue-600 text-white rounded px-3 py-1">Copiar</button>
        <a href="/m/freezer" target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline">abrir</a>
      </div>
      {isLoading ? <div className="text-gray-400 text-sm">Carregando...</div>
        : lista.length === 0 ? <div className="text-gray-400 text-sm py-8 text-center">Nenhum pré-cadastro pendente.</div>
        : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b">
            <th className="py-2">Cliente</th><th>Patrimônio</th><th>Modelo</th><th>Vendedor</th><th>Quando</th><th></th>
          </tr></thead>
          <tbody>
            {lista.map((p: any) => (
              <tr key={p.id} className="border-b">
                <td className="py-2">
                  <div className="font-medium">{p.cliente_nome}</div>
                  {p.cliente_codigo && <div className="text-xs text-gray-500">Cód. {p.cliente_codigo}</div>}
                  {!p.cliente_id && <span className="text-[10px] text-amber-600">não vinculado ao cadastro</span>}
                </td>
                <td className="font-bold">{p.numero_patrimonio}</td>
                <td>{p.modelo || '—'}</td>
                <td>{p.vendedor_nome || '—'}</td>
                <td className="text-xs text-gray-500">{p.criado_em ? new Date(p.criado_em).toLocaleString('pt-BR') : ''}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => aprovar.mutate(p.id)} disabled={aprovar.isPending}
                    className="text-emerald-600 text-xs font-medium mr-3">Aprovar</button>
                  <button onClick={() => rejeitar.mutate(p.id)} disabled={rejeitar.isPending}
                    className="text-red-500 text-xs font-medium">Rejeitar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {lista.some((p: any) => !p.cliente_id) && (
        <p className="text-xs text-amber-600">⚠ "não vinculado" = o vendedor digitou o nome mas não casou com o cadastro; ao aprovar o freezer nasce livre (sem cliente). Coloque no cliente depois via Solicitações.</p>
      )}
    </div>
  )
}

// ---------------- Modal de modelo (incluir/editar) ----------------
function ModeloModal({ modelo, onClose, onSaved }: { modelo?: any; onClose: () => void; onSaved: () => void }) {
  const ed = !!modelo?.produto_id
  const [f, setF] = useState<any>({
    codigo: modelo?.codigo || '', descricao: modelo?.descricao || '',
    preco: modelo?.preco ?? '', marca: modelo?.marca || '', ncm: modelo?.ncm || '',
  })
  const set = (k: string, v: any) => setF((s: any) => ({ ...s, [k]: v }))
  const salvar = useMutation({
    mutationFn: async () => {
      const body = { codigo: f.codigo, descricao: f.descricao, preco: Number(f.preco || 0), marca: f.marca || null, ncm: f.ncm || null }
      return ed
        ? (await api.put(`/comodato/modelos/${modelo.produto_id}`, body)).data
        : (await api.post('/comodato/modelos', body)).data
    },
    onSuccess: () => { toast.success(ed ? 'Modelo atualizado' : 'Modelo criado'); onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{ed ? 'Editar modelo' : 'Novo modelo'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Código</label><input className="input" value={f.codigo} onChange={e => set('codigo', e.target.value)} /></div>
          <div><label className="label">Preço (R$)</label><input type="number" step="0.01" className="input" value={f.preco} onChange={e => set('preco', e.target.value)} /></div>
          <div className="col-span-2"><label className="label">Descrição</label><input className="input" value={f.descricao} onChange={e => set('descricao', e.target.value)} /></div>
          <div><label className="label">Marca</label><input className="input" value={f.marca} onChange={e => set('marca', e.target.value)} /></div>
          <div><label className="label">NCM</label><input className="input" value={f.ncm} onChange={e => set('ncm', e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded border">Cancelar</button>
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending || !f.codigo || !f.descricao}
            className="px-4 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50">
            {salvar.isPending ? '…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------- Precificação por modelo (com CRUD) ----------------
function Precificacao() {
  const qc = useQueryClient()
  const { data: modelos = [], isLoading } = useQuery<any[]>({
    queryKey: ['modelos-precos'],
    queryFn: async () => (await api.get('/comodato/modelos-precos')).data,
  })
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const refresh = () => { qc.invalidateQueries({ queryKey: ['modelos-precos'] }); qc.invalidateQueries({ queryKey: ['freezers'] }) }
  // estado local de edição de preço por produto_id
  const [draft, setDraft] = useState<Record<number, { preco: string; aplicar: string }>>({})
  const getD = (m: any) => draft[m.produto_id] || { preco: String(m.preco ?? ''), aplicar: 'nenhum' }
  const setD = (m: any, patch: any) => setDraft(s => ({ ...s, [m.produto_id]: { ...getD(m), ...patch } }))

  const salvar = useMutation({
    mutationFn: async (m: any) => {
      const d = getD(m)
      return (await api.put(`/comodato/modelos-precos/${m.produto_id}`, { preco: Number(d.preco || 0), aplicar: d.aplicar })).data
    },
    onSuccess: (r: any) => {
      toast.success(r.freezers_atualizados ? `Preço salvo · ${r.freezers_atualizados} freezer(s) atualizado(s)` : 'Preço do modelo salvo')
      refresh()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar preço'),
  })
  const excluir = useMutation({
    mutationFn: async (m: any) => (await api.delete(`/comodato/modelos/${m.produto_id}`)).data,
    onSuccess: (r: any) => { toast.success(r.modo === 'inativado' ? 'Modelo inativado (tinha freezers)' : 'Modelo excluído'); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  if (isLoading) return <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" /> Carregando…</div>

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex-1">
          Defina o <b>valor do bem por modelo</b>. No cadastro de um freezer o valor já vem do modelo.
          Use <b>“preencher vazios”</b> ou <b>“sobrescrever todos”</b> pra aplicar aos freezers.
        </div>
        <button onClick={() => { setEditing(null); setModal(true) }}
          className="px-3 py-2 text-sm rounded bg-blue-600 text-white flex items-center gap-1 shrink-0">
          <PlusCircle size={16} /> Novo modelo
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-2 px-3">Modelo</th>
            <th className="text-center">Freezers</th>
            <th className="text-right px-3">Preço do modelo (R$)</th>
            <th className="px-3">Aplicar aos freezers</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {modelos.length === 0 && (
            <tr><td colSpan={5} className="py-8 text-center text-gray-400">Nenhum modelo. Clique em <b>Novo modelo</b>.</td></tr>
          )}
          {modelos.map((m: any) => {
            const d = getD(m)
            return (
              <tr key={m.produto_id} className="border-b hover:bg-gray-50">
                <td className="py-2 px-3">
                  <div className="font-medium">{m.descricao}</div>
                  <div className="text-xs text-gray-400">{m.codigo}</div>
                </td>
                <td className="text-center">
                  {m.qtd_freezers}
                  {m.qtd_divergentes > 0 && (
                    <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                      title="Freezers com valor diferente do preço do modelo">{m.qtd_divergentes} divergente(s)</span>
                  )}
                </td>
                <td className="text-right px-3">
                  <input type="number" step="0.01" className="input !py-1 text-right w-32"
                    value={d.preco} onChange={e => setD(m, { preco: e.target.value })} />
                </td>
                <td className="px-3">
                  <select className="input !py-1 text-sm" value={d.aplicar} onChange={e => setD(m, { aplicar: e.target.value })}>
                    <option value="nenhum">Só salvar o preço</option>
                    <option value="vazios">Preencher só os vazios</option>
                    <option value="todos">Sobrescrever todos</option>
                  </select>
                </td>
                <td className="text-right px-3 whitespace-nowrap">
                  <button onClick={() => salvar.mutate(m)} disabled={salvar.isPending}
                    className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50">
                    {salvar.isPending && salvar.variables?.produto_id === m.produto_id ? '…' : 'Salvar preço'}
                  </button>
                  <button onClick={() => { setEditing(m); setModal(true) }} title="Editar modelo"
                    className="ml-1 p-1.5 text-gray-500 hover:text-blue-600"><Pencil size={15} /></button>
                  <button onClick={() => pedirExclusao({ descricao: `o modelo "${m.descricao}"`, onConfirm: () => excluir.mutate(m) })} title="Excluir modelo"
                    className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 size={15} /></button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {modal && <ModeloModal modelo={editing} onClose={() => { setModal(false); setEditing(null) }} onSaved={refresh} />}
    </div>
  )
}

// ---------------- Depósitos de comodato ----------------
function Depositos() {
  const qc = useQueryClient()
  const { data: deps = [], isLoading } = useQuery<any[]>({
    queryKey: ['comodato-depositos'],
    queryFn: async () => (await api.get('/comodato/depositos')).data,
  })
  const [nome, setNome] = useState('')
  const [editId, setEditId] = useState<number | null>(null)
  const [editNome, setEditNome] = useState('')
  const refresh = () => qc.invalidateQueries({ queryKey: ['comodato-depositos'] })
  const criar = useMutation({
    mutationFn: async () => (await api.post('/comodato/depositos', { nome })).data,
    onSuccess: () => { toast.success('Depósito criado'); setNome(''); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const salvar = useMutation({
    mutationFn: async (d: any) => (await api.put(`/comodato/depositos/${d.id}`, { nome: editNome, ativo: true })).data,
    onSuccess: () => { toast.success('Depósito salvo'); setEditId(null); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/comodato/depositos/${id}`)).data,
    onSuccess: (r: any) => { toast.success(r.modo === 'inativado' ? 'Depósito inativado (em uso)' : 'Depósito excluído'); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  if (isLoading) return <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" /> Carregando…</div>
  return (
    <div className="space-y-3 max-w-2xl">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        Cadastre os <b>depósitos</b> onde os freezers ficam disponíveis. Ao lançar uma colocação,
        você informa de qual depósito o freezer está saindo.
      </div>
      <div className="flex gap-2">
        <input className="input flex-1" placeholder="Nome do depósito (ex.: CD Crato)" value={nome}
          onChange={e => setNome(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && nome.trim()) criar.mutate() }} />
        <button onClick={() => criar.mutate()} disabled={!nome.trim() || criar.isPending}
          className="px-3 py-2 text-sm rounded bg-blue-600 text-white flex items-center gap-1 disabled:opacity-50">
          <PlusCircle size={16} /> Incluir
        </button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 border-b"><th className="py-2 px-3">Depósito</th><th className="text-center">Freezers</th><th></th></tr></thead>
        <tbody>
          {deps.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-gray-400">Nenhum depósito cadastrado.</td></tr>}
          {deps.map((d: any) => (
            <tr key={d.id} className="border-b hover:bg-gray-50">
              <td className="py-2 px-3">
                {editId === d.id
                  ? <input className="input !py-1" value={editNome} onChange={e => setEditNome(e.target.value)} autoFocus />
                  : <span className="font-medium flex items-center gap-2"><Warehouse size={15} className="text-gray-400" /> {d.nome}</span>}
              </td>
              <td className="text-center">{d.qtd_freezers}</td>
              <td className="text-right px-3 whitespace-nowrap">
                {editId === d.id ? (
                  <>
                    <button onClick={() => salvar.mutate(d)} className="px-3 py-1 text-sm rounded bg-blue-600 text-white">Salvar</button>
                    <button onClick={() => setEditId(null)} className="ml-1 px-2 py-1 text-sm rounded border">Cancelar</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setEditId(d.id); setEditNome(d.nome) }} title="Editar" className="p-1.5 text-gray-500 hover:text-blue-600"><Pencil size={15} /></button>
                    <button onClick={() => pedirExclusao({ descricao: `o depósito "${d.nome}"`, onConfirm: () => excluir.mutate(d.id) })} title="Excluir" className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 size={15} /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ComodatoPage() {
  const [tab, setTab] = useState<'painel' | 'freezers' | 'preco' | 'depositos' | 'solic' | 'mapa' | 'precad'>('painel')
  const { data: cont } = useQuery<{ pendentes: number }>({
    queryKey: ['precad-cont'],
    queryFn: async () => (await api.get('/comodato/pre-cadastros/pendentes-contagem')).data,
    refetchInterval: 60000,
  })
  const pend = cont?.pendentes || 0
  const tabs = [
    { k: 'painel', label: 'Painel' }, { k: 'freezers', label: 'Freezers' },
    { k: 'preco', label: 'Precificação' },
    { k: 'depositos', label: 'Depósitos' },
    { k: 'mapa', label: 'Mapa' }, { k: 'solic', label: 'Solicitações' },
    { k: 'precad', label: pend ? `Pré-cadastros (${pend})` : 'Pré-cadastros' },
  ] as const
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Snowflake className="text-blue-500" />
        <h1 className="text-xl font-bold">Comodato de Freezers</h1>
      </div>
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'painel' && <Painel />}
      {tab === 'freezers' && <Freezers />}
      {tab === 'preco' && <Precificacao />}
      {tab === 'depositos' && <Depositos />}
      {tab === 'mapa' && <Mapa />}
      {tab === 'solic' && <Solicitacoes />}
      {tab === 'precad' && <PreCadastros />}
    </div>
  )
}
